import { AxiosResponse } from "axios";
import { API } from "../index";

export type AppNotification = {
  id: number;
  title: string;
  body: string;
  created_at: string;
  data: Record<string, any> | null;
  status: string;
  read_at: string | null;
  web_url: string;
  app_url: string;
};

export type NotificationListResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: AppNotification[];
};

export type NotificationCountResponse = {
  count: number;
};

export type NotificationPreference = {
  category: "payment" | "usage" | "site_notice" | "marketing" | "mandatory";
  label: string;
  site_enabled: boolean;
  is_mandatory: boolean;
  is_enabled: boolean;
};

export type WebPushSubscriptionStatus = {
  is_subscribed: boolean;
  subscription: {
    id: number;
    is_active: boolean;
    last_seen_at: string;
    last_failure_at: string | null;
    failure_count: number;
  } | null;
};

export const APP_NOTIFICATION_QUERY_KEY = ["app-notifications"] as const;
export const NOTIFICATION_COUNT_QUERY_KEY = ["notification-count"] as const;

export class WebPushPermissionError extends Error {
  permission: NotificationPermission;

  constructor(permission: NotificationPermission) {
    super("알림 권한이 허용되지 않았습니다.");
    this.name = "WebPushPermissionError";
    this.permission = permission;
  }
}

let webPushMutationQueue: Promise<void> = Promise.resolve();

function enqueueWebPushMutation<T>(operation: () => Promise<T>): Promise<T> {
  const result = webPushMutationQueue.then(operation, operation);
  webPushMutationQueue = result.then(
    () => undefined,
    () => undefined,
  );
  return result;
}

async function getCurrentWebPushSubscription(): Promise<PushSubscription | null> {
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

export const notificationService = {
  getNotifications: async (
    page = 1
  ): Promise<AxiosResponse<NotificationListResponse>> => {
    return await API.get(`/api/v2/notifications/?page=${page}`);
  },

  getNotificationCount: async (): Promise<AxiosResponse<NotificationCountResponse>> => {
    return await API.get("/api/v2/notifications/unread-count/");
  },

  readNotificationById: async (id: number): Promise<AxiosResponse<{ result: string }>> => {
    return await API.put(`/api/v2/notifications/${id}/read/`, {});
  },

  readAllNotifications: async (): Promise<AxiosResponse<{ result: string }>> => {
    return await API.put("/api/v2/notifications/read-all/", {});
  },

  getPreferences: async (): Promise<AxiosResponse<{ site_id: number; categories: NotificationPreference[] }>> => {
    return API.get("/api/v2/notifications/preferences/");
  },

  updatePreference: async (category: NotificationPreference["category"], is_enabled: boolean) => {
    return API.put("/api/v2/notifications/preferences/", { category, is_enabled });
  },

  getWebPushSubscriptionStatus: async (): Promise<AxiosResponse<WebPushSubscriptionStatus>> => {
    return API.get("/api/v2/notifications/web-push/subscription/");
  },

  isWebPushSupported: (): boolean => {
    return (
      typeof window !== "undefined" &&
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window
    );
  },

  getCurrentWebPushSubscription: async (): Promise<PushSubscription | null> => {
    if (!notificationService.isWebPushSupported()) return null;
    await webPushMutationQueue;
    return getCurrentWebPushSubscription();
  },

  subscribeToWebPush: async (): Promise<WebPushSubscriptionStatus> => {
    return enqueueWebPushMutation(async () => {
      if (!notificationService.isWebPushSupported()) {
        throw new Error("이 브라우저에서는 Web Push를 사용할 수 없습니다.");
      }
      const config = await API.get<{ is_configured: boolean; public_key: string | null }>("/api/v2/notifications/web-push/config/");
      if (!config.data.is_configured || !config.data.public_key) {
        throw new Error("Web Push를 사용할 수 없습니다.");
      }
      const permission = window.Notification.permission === "granted"
        ? "granted"
        : await window.Notification.requestPermission();
      if (permission !== "granted") {
        throw new WebPushPermissionError(permission);
      }
      const registration = await navigator.serviceWorker.ready;
      const subscription = (
        await registration.pushManager.getSubscription()
        ?? await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(config.data.public_key),
        })
      );
      const json = subscription.toJSON();
      const response = await API.put<WebPushSubscriptionStatus>("/api/v2/notifications/web-push/subscription/", {
        endpoint: subscription.endpoint,
        p256dh: json.keys?.p256dh,
        auth: json.keys?.auth,
      });
      return response.data;
    });
  },

  unsubscribeFromWebPush: async (): Promise<void> => {
    return enqueueWebPushMutation(async () => {
      if (!notificationService.isWebPushSupported()) return;
      const subscription = await getCurrentWebPushSubscription();
      if (!subscription) return;
      await API.delete("/api/v2/notifications/web-push/subscription/", { data: { endpoint: subscription.endpoint } });
      await subscription.unsubscribe();
    });
  },
};

function urlBase64ToUint8Array(value: string): Uint8Array {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const normalized = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(normalized);
  return Uint8Array.from(raw, (character) => character.charCodeAt(0));
}
